// Intake Receiver — the PUBLIC, unauthenticated client intake form.
//
// An outside person opens /square/intake/:code (no Square Code login).
// The invite_code is the credential. This page fetches the invite's
// non-secret form config (api.intake.publicGet), renders a progressive
// form, and submits (api.intake.publicSubmit) into the operator's
// review queue. It deliberately shows NO operator nav, NO command-center
// shell — just the brand-consistent intake surface.
//
// Honesty: attachments are captured as metadata only (no public upload
// route). An unknown/expired/closed invite shows an honest blocked
// state, never a fake form. Nothing here calls a provider or sends mail.

import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { api } from '../../api/client'
import {
  WORKSPACE_ACCENTS, WORKSPACE_FONT, WORKSPACE_TEXT, WORKSPACE_SURFACE, WORKSPACE_GRID_URL, workspaceTint,
} from '@square/ui'

const ACCENT = WORKSPACE_ACCENTS.cyan

interface InviteConfig {
  invite_code: string
  invite_type: string
  title: string
  description: string | null
  questions: Array<Record<string, unknown>>
  allow_attachments: boolean
}

type Phase = 'loading' | 'ready' | 'blocked' | 'submitting' | 'done'

const fieldStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: 14,
  background: 'rgba(0,0,0,0.3)', color: WORKSPACE_TEXT.primary,
  border: `1px solid ${workspaceTint(ACCENT, 18)}`, borderRadius: 8, fontFamily: WORKSPACE_FONT.ui, outline: 'none',
}
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
  color: WORKSPACE_TEXT.tertiary, fontFamily: WORKSPACE_FONT.mono, marginBottom: 6,
}

export default function IntakeReceiver() {
  const { code = '' } = useParams<{ code: string }>()
  const [phase, setPhase] = useState<Phase>('loading')
  const [invite, setInvite] = useState<InviteConfig | null>(null)
  const [blockedReason, setBlockedReason] = useState<string>('not_found')
  const [error, setError] = useState<string | null>(null)

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [description, setDescription] = useState('')
  const [files, setFiles] = useState<Array<{ name: string; size: string; type: string }>>([])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const result = await api.intake.publicGet(code)
        if (cancelled) return
        if (result.ok) { setInvite(result.invite); setPhase('ready') }
        else { setBlockedReason(result.reason); setPhase('blocked') }
      } catch {
        if (!cancelled) { setBlockedReason('not_found'); setPhase('blocked') }
      }
    })()
    return () => { cancelled = true }
  }, [code])

  const onFiles = useCallback((list: FileList | null) => {
    if (!list) return
    const meta = Array.from(list).slice(0, 10).map((f) => ({
      name: f.name, size: `${Math.round(f.size / 1024)} KB`, type: f.type || 'unknown',
    }))
    setFiles(meta)
  }, [])

  const submit = useCallback(async () => {
    if (!name.trim() || description.trim().length < 10) {
      setError('Please add your name and a description of at least 10 characters.')
      return
    }
    setError(null)
    setPhase('submitting')
    try {
      const result = await api.intake.publicSubmit({
        code, name: name.trim(), email: email.trim(), description: description.trim(),
        attachments: files,
      })
      if (result.ok) setPhase('done')
      else { setBlockedReason(result.reason ?? 'closed'); setPhase('blocked') }
    } catch {
      setError('Something went wrong submitting your information. Please try again.')
      setPhase('ready')
    }
  }, [code, name, email, description, files])

  const page = (children: React.ReactNode) => (
    <div style={{ minHeight: '100vh', background: WORKSPACE_SURFACE.void ?? '#08040f', backgroundImage: WORKSPACE_GRID_URL, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 620, background: WORKSPACE_SURFACE.card, border: `1px solid ${WORKSPACE_SURFACE.line}`, borderRadius: 16, padding: '32px 30px', boxShadow: '0 24px 80px rgba(0,0,0,0.5)' }}>
        {children}
      </div>
    </div>
  )

  if (phase === 'loading') {
    return page(<div style={{ fontSize: 13, color: WORKSPACE_TEXT.muted, fontFamily: WORKSPACE_FONT.mono, letterSpacing: '0.12em' }}>LOADING…</div>)
  }

  if (phase === 'blocked') {
    const msg = blockedReason === 'expired'
      ? 'This intake link has expired. Please contact the person who sent it for a new one.'
      : blockedReason === 'closed'
        ? 'This intake link is no longer accepting submissions.'
        : 'This intake link is not valid. Please check the link or contact the person who sent it.'
    return page(
      <div>
        <div style={{ fontSize: 10, letterSpacing: '0.28em', color: WORKSPACE_TEXT.tertiary, fontFamily: WORKSPACE_FONT.mono, marginBottom: 10 }}>OLIVIUM · INTAKE</div>
        <div style={{ fontSize: 20, fontWeight: 300, color: WORKSPACE_TEXT.primary, marginBottom: 10 }}>Link unavailable</div>
        <div style={{ fontSize: 13.5, lineHeight: 1.7, color: WORKSPACE_TEXT.secondary, fontFamily: WORKSPACE_FONT.ui }}>{msg}</div>
      </div>,
    )
  }

  if (phase === 'done') {
    // Olivium secure-intake confirmation — quantum grid + glow frame,
    // a reference code, receipt status chips (received / sealed / queued),
    // and an honest "what happens next". No claim of email/calendar
    // follow-up (none happened).
    const seals: Array<{ label: string; on: boolean }> = [
      { label: 'RECEIVED', on: true },
      { label: 'SEALED', on: true },
      { label: 'QUEUED FOR REVIEW', on: true },
    ]
    return (
      <div style={{ minHeight: '100vh', background: WORKSPACE_SURFACE.void ?? '#08040f', backgroundImage: WORKSPACE_GRID_URL, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div
          style={{
            position: 'relative', width: '100%', maxWidth: 640, borderRadius: 20,
            padding: '2px',
            background: `linear-gradient(140deg, ${workspaceTint(ACCENT, 55)}, ${workspaceTint(WORKSPACE_ACCENTS.purple, 40)} 55%, ${workspaceTint(ACCENT, 20)})`,
            boxShadow: `0 0 60px ${workspaceTint(ACCENT, 22)}, 0 24px 90px rgba(0,0,0,0.6)`,
          }}
        >
          <div style={{ borderRadius: 18, background: 'linear-gradient(180deg, rgba(20,10,32,0.96), rgba(10,5,20,0.98))', backgroundImage: WORKSPACE_GRID_URL, padding: '40px 36px', textAlign: 'center' }}>
            {/* Seal mark */}
            <div style={{ width: 62, height: 62, margin: '0 auto 18px', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', background: workspaceTint(ACCENT, 12), border: `1px solid ${workspaceTint(ACCENT, 34)}`, boxShadow: `0 0 24px ${workspaceTint(ACCENT, 26)}` }}>
              <span style={{ fontSize: 26, color: ACCENT }}>◈</span>
            </div>
            <div style={{ fontSize: 10, letterSpacing: '0.34em', color: ACCENT, fontFamily: WORKSPACE_FONT.mono, marginBottom: 12 }}>OLIVIUM · SECURE INTAKE</div>
            <div style={{ fontSize: 26, fontWeight: 300, letterSpacing: '-0.01em', color: WORKSPACE_TEXT.primary, marginBottom: 12 }}>Secure intake received</div>
            <div style={{ fontSize: 14, lineHeight: 1.75, color: WORKSPACE_TEXT.secondary, fontFamily: WORKSPACE_FONT.ui, maxWidth: 460, margin: '0 auto 22px' }}>
              Your information has been received and queued for review. The team will review the submission and follow up if more information is needed.
            </div>

            {/* Receipt status chips */}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 22 }}>
              {seals.map((s) => (
                <span key={s.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 20, background: workspaceTint(WORKSPACE_ACCENTS.green, 10), border: `1px solid ${workspaceTint(WORKSPACE_ACCENTS.green, 26)}`, color: WORKSPACE_ACCENTS.green, fontSize: 9.5, fontWeight: 700, fontFamily: WORKSPACE_FONT.mono, letterSpacing: '0.08em' }}>
                  <span aria-hidden style={{ width: 5, height: 5, borderRadius: '50%', background: WORKSPACE_ACCENTS.green, boxShadow: `0 0 8px ${WORKSPACE_ACCENTS.green}` }} />
                  {s.label}
                </span>
              ))}
            </div>

            {/* Reference code */}
            {code && (
              <div style={{ display: 'inline-block', padding: '10px 18px', borderRadius: 10, background: 'rgba(0,0,0,0.35)', border: `1px solid ${workspaceTint(ACCENT, 18)}`, marginBottom: 24 }}>
                <div style={{ fontSize: 8.5, letterSpacing: '0.24em', color: WORKSPACE_TEXT.tertiary, fontFamily: WORKSPACE_FONT.mono, marginBottom: 4 }}>REFERENCE CODE</div>
                <div style={{ fontSize: 14, color: ACCENT, fontFamily: WORKSPACE_FONT.mono, fontWeight: 700, letterSpacing: '0.04em' }}>{code}</div>
              </div>
            )}

            {/* What happens next */}
            <div style={{ textAlign: 'left', maxWidth: 460, margin: '0 auto', borderTop: `1px solid ${WORKSPACE_SURFACE.line}`, paddingTop: 18 }}>
              <div style={{ fontSize: 9, letterSpacing: '0.24em', color: WORKSPACE_TEXT.tertiary, fontFamily: WORKSPACE_FONT.mono, marginBottom: 10 }}>WHAT HAPPENS NEXT</div>
              {[
                'Your submission is sealed in the Olivium review queue.',
                'An operator reviews it and may turn it into a project.',
                'If more detail is needed, the team will reach out to you directly.',
              ].map((line, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 8 }}>
                  <span style={{ fontSize: 10, color: ACCENT, marginTop: 3, flexShrink: 0 }}>▸</span>
                  <span style={{ fontSize: 12.5, lineHeight: 1.6, color: WORKSPACE_TEXT.secondary, fontFamily: WORKSPACE_FONT.ui }}>{line}</span>
                </div>
              ))}
            </div>

            <div style={{ fontSize: 10.5, color: WORKSPACE_TEXT.muted, fontFamily: WORKSPACE_FONT.ui, marginTop: 22 }}>
              No account required. Secured by Olivium — your information goes only to the team that sent you this link.
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ready | submitting
  const busy = phase === 'submitting'
  return page(
    <div>
      <div style={{ fontSize: 10, letterSpacing: '0.28em', color: ACCENT, fontFamily: WORKSPACE_FONT.mono, marginBottom: 10 }}>
        OLIVIUM · {String(invite?.invite_type ?? 'intake').toUpperCase()} INTAKE
      </div>
      <div style={{ fontSize: 24, fontWeight: 300, letterSpacing: '-0.01em', color: WORKSPACE_TEXT.primary, marginBottom: 8 }}>{invite?.title}</div>
      {invite?.description && <div style={{ fontSize: 13.5, lineHeight: 1.7, color: WORKSPACE_TEXT.secondary, fontFamily: WORKSPACE_FONT.ui, marginBottom: 22 }}>{invite.description}</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label style={labelStyle} htmlFor="intk-name">Your name</label>
          <input id="intk-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" style={fieldStyle} disabled={busy} />
        </div>
        <div>
          <label style={labelStyle} htmlFor="intk-email">Email</label>
          <input id="intk-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" style={fieldStyle} disabled={busy} />
        </div>
        <div>
          <label style={labelStyle} htmlFor="intk-desc">Tell us about your project</label>
          <textarea id="intk-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={5} placeholder="Describe what you need — goals, scope, timeline, anything useful." style={{ ...fieldStyle, resize: 'vertical', lineHeight: 1.6 }} disabled={busy} />
        </div>
        {invite?.allow_attachments && (
          <div>
            <label style={labelStyle} htmlFor="intk-files">Attachments</label>
            <input id="intk-files" type="file" multiple onChange={(e) => onFiles(e.target.files)} disabled={busy} style={{ fontSize: 12, color: WORKSPACE_TEXT.secondary }} />
            {files.length > 0 && (
              <div style={{ fontSize: 11, color: WORKSPACE_TEXT.muted, marginTop: 6, fontFamily: WORKSPACE_FONT.mono }}>
                {files.map((f) => f.name).join(', ')} — captured as metadata only (files are not uploaded).
              </div>
            )}
          </div>
        )}

        {error && <div style={{ fontSize: 12.5, color: WORKSPACE_ACCENTS.red, fontFamily: WORKSPACE_FONT.ui }}>{error}</div>}

        <button
          type="button"
          onClick={() => void submit()}
          disabled={busy}
          style={{
            marginTop: 4, padding: '12px 18px', borderRadius: 10, cursor: busy ? 'wait' : 'pointer',
            background: workspaceTint(ACCENT, 16), border: `1px solid ${workspaceTint(ACCENT, 32)}`,
            color: ACCENT, fontSize: 13, fontWeight: 800, fontFamily: WORKSPACE_FONT.mono, letterSpacing: '0.06em', opacity: busy ? 0.6 : 1,
          }}
        >
          {busy ? 'SUBMITTING…' : 'SUBMIT'}
        </button>
        <div style={{ fontSize: 10.5, color: WORKSPACE_TEXT.muted, fontFamily: WORKSPACE_FONT.ui, textAlign: 'center' }}>
          Secured by Olivium. Your information goes only to the team that sent you this link.
        </div>
      </div>
    </div>,
  )
}
